/**
 * UO交易大厅 · 交易详情（legacy parity）
 */
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { otcAPI, type OtcHallDetail } from '../api/otc';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { ThemedAvatar } from '../components/ui/ThemedSurface';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { QueryErrorNote } from '../components/ui/QueryErrorNote';
import { OTC_COLORS as C } from '@/constants';
import { tokenDisplayLabel } from '@/utils/orb-display';

const ORB = tokenDisplayLabel('uo');

function fmtNum(v: string | undefined, digits = 4): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

function fmtTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.replace('T', ' ').slice(0, 19);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
}

function fmtLegacyTime(value: string): string {
  const s = value.trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) return s;
  return fmtTime(s);
}

function StatBox({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className="min-w-0 rounded-xl px-2 py-2 text-center" style={{ background: C.chipBg }}>
      <div className="truncate text-[10px]" style={{ color: C.muted }}>
        {label}
      </div>
      <div className="mt-1 truncate font-mono text-sm font-semibold" style={{ color: C.gold }}>
        {value}
        {suffix ? <span className="ml-0.5 text-[10px] font-normal">{suffix}</span> : null}
      </div>
    </div>
  );
}

function DetailBody({ detail }: { detail: OtcHallDetail }) {
  const { t } = useTranslation();
  const listTime = detail.create_time ? fmtLegacyTime(detail.create_time) : '';

  return (
    <div className="space-y-4 px-4 pb-8 pt-[68px]">
      <section className="rounded-2xl px-4 py-4" style={{ background: C.innerBg, border: `1px solid ${C.line}` }}>
        <div className="mb-3 text-sm font-semibold" style={{ color: C.text }}>
          {t('uoTradingHallDetail.sellerInfo', '卖家信息')}
        </div>
        <div className="flex flex-col items-center gap-2 py-2">
          <ThemedAvatar src={detail.seller.avatar} seed={detail.seller.display_id} className="h-[72px] w-[72px]" />
          <span className="font-mono text-lg font-semibold" style={{ color: C.gold }}>
            {detail.seller.display_id}
          </span>
        </div>
      </section>

      <section className="rounded-2xl px-4 py-4" style={{ background: C.innerBg, border: `1px solid ${C.line}` }}>
        <div className="mb-3 text-sm font-semibold" style={{ color: C.text }}>
          {t('uoTradingHallDetail.listingInfo', '挂卖信息')}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <StatBox label={t('common.sellTotal', '挂卖总数')} value={fmtNum(detail.listed_uo, 0)} suffix={ORB} />
          <StatBox label={t('uoTradingHallDetail.burnUo', '交易销毁')} value={fmtNum(detail.burn_uo, 0)} suffix={ORB} />
          <StatBox label={t('common.filled', '已成交')} value={fmtNum(detail.traded_uo, 0)} suffix={ORB} />
          <StatBox
            label={t('uoTradingHallDetail.dealPrice', '成交价')}
            value={fmtNum(detail.unit_price, 3)}
            suffix="$"
          />
          <StatBox
            label={t('uoTradingHallDetail.dealValue', '成交价值')}
            value={fmtNum(detail.value_ap, 3)}
            suffix="$"
          />
          <StatBox label={t('uoTradingHallDetail.pending', '待成交')} value={fmtNum(detail.pending_uo, 0)} suffix={ORB} />
        </div>
        {listTime ? (
          <div className="mt-3 text-right text-[11px]" style={{ color: C.muted }}>
            {listTime}
          </div>
        ) : null}
      </section>

      <section className="rounded-2xl px-3 py-4" style={{ background: C.innerBg, border: `1px solid ${C.line}` }}>
        <div className="mb-3 px-1 text-sm font-semibold" style={{ color: C.text }}>
          {t('uoTradingHallDetail.buyerList', '买家列表')}
        </div>
        <div
          className="grid grid-cols-[28px_1fr_56px_56px_1fr] gap-1 px-1 pb-2 text-[10px]"
          style={{ color: C.muted }}
        >
          <span>#</span>
          <span>{t('uoTradingHallDetail.buyer', '买家')}</span>
          <span className="text-center">{t('uoTradingHallDetail.qty', '数量')}</span>
          <span className="text-center">{t('uoTradingHallDetail.dealPrice', '成交价')}</span>
          <span className="text-right">{t('common.time', '时间')}</span>
        </div>
        {detail.buyers.length === 0 ? (
          <div className="px-1 py-6 text-center text-xs" style={{ color: C.muted }}>
            {t('uoTradingHallDetail.noBuyers', '暂无买家记录')}
          </div>
        ) : (
          <div className="space-y-3">
            {detail.buyers.map((row) => (
              <div
                key={`${row.index}-${row.deal_time}-${row.quantity_uo}`}
                className="grid grid-cols-[28px_1fr_56px_56px_1fr] items-center gap-1 border-t px-1 py-2"
                style={{ borderColor: C.line }}
              >
                <span className="font-mono text-xs" style={{ color: C.gold }}>
                  {row.index}
                </span>
                <div className="flex min-w-0 items-center gap-2">
                  <ThemedAvatar src={row.avatar} seed={row.buyer_id} className="h-7 w-7" />
                  <span className="truncate font-mono text-xs font-semibold" style={{ color: C.gold }}>
                    {row.buyer_id}
                  </span>
                </div>
                <span className="text-center font-mono text-xs" style={{ color: C.gold }}>
                  {fmtNum(row.quantity_uo, 0)}
                </span>
                <span className="text-center font-mono text-xs" style={{ color: C.gold }}>
                  {fmtNum(row.unit_price, 4)}
                </span>
                <span className="truncate text-right text-[10px]" style={{ color: C.muted }}>
                  {fmtLegacyTime(row.deal_time)}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function UoTradingHallDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();

  const detailQuery = useQuery({
    queryKey: ['otc', 'hall', 'detail', id],
    queryFn: () => otcAPI.getHallDetail(id),
    enabled: Boolean(id),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  const showSkeleton = detailQuery.isPending && !detailQuery.data;

  const headerTheme = {
    topbarBg: C.topbarBg,
    titleGradient: C.titleGradient,
    text: C.text,
  };

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader
          title={t('uoTradingHallDetail.title', '交易详情')}
          onBack={() => navigate(-1)}
          theme={headerTheme}
        />

        {showSkeleton ? (
          <div className="space-y-3 px-4 pt-[68px]">
            <LoadingSkeleton className="h-36" />
            <LoadingSkeleton className="h-44" />
            <LoadingSkeleton className="h-52" />
          </div>
        ) : detailQuery.isError ? (
          <div className="px-4 pt-[68px]">
            <QueryErrorNote
              message={t('uoTradingHallDetail.loadFailed', '加载失败')}
              onRetry={() => void detailQuery.refetch()}
            />
          </div>
        ) : detailQuery.data ? (
          <DetailBody detail={detailQuery.data} />
        ) : null}
      </div>
    </div>
  );
}
